import { EVENTS, TYPES } from './floating-label';

export { EVENTS, TYPES };

/**
 * Defines default options for state modifiers
 * @type {Object}
 */
const DEFAULT_STATE_MODIFIERS = {
  filledModifier: 'filled',
  invalidModifier: 'invalid',
  selectedModifier: 'selected',
};

/**
 * Adds modifier CSS class names to any of the elements.
 * @param {Array} nodes - An array of objects containing a name and element
 * @param {...string} modifiers - Modifier names
 */
const addClassNames = (nodes, ...modifiers) => {
  nodes.forEach(({ name, node }) => {
    modifiers.forEach(modifierName =>
      node.classList.add(`${name}--${modifierName}`),
    );
  });
};

/**
 * Removes modifier CSS class names to any of the elements.
 * @param {Array} nodes - An array of objects containing a name and element
 * @param {...string} modifiers - Modifier names
 */
const removeClassNames = (nodes, ...modifiers) => {
  nodes.forEach(({ name, node }) => {
    modifiers.forEach(modifierName =>
      node.classList.remove(`${name}--${modifierName}`),
    );
  });
};

/**
 * Ran whenever a FIELD is in a completed state, i.e. when the page first loads
 * or when a blur event occurred.
 * @param {Array} nodes - An array of objects containing a name and element
 * @param {HTMLElement} target - The element receiving the event
 * @param {string} selectedModifier - The selected modifier value
 * @param {string} filledModifier - The filled modifier value
 * @param {string} invalidModifier - The invalid modifier value
 */
const completed = (nodes, target, selectedModifier, filledModifier, invalidModifier) => {
  const [{ node: container }] = nodes.filter(({ type }) => type === TYPES.CONTAINER);
  removeClassNames(nodes, selectedModifier, filledModifier, invalidModifier);

  if (container.querySelector(':invalid') === target) {
    addClassNames(nodes, invalidModifier);
  }

  if (target.value) {
    addClassNames(nodes, filledModifier);
  }
};

/**
 * Responds to any event based on the type of event.
 * @param {string} type - The type of event, i.e. focus, blur
 * @param {HTMLElement} target - The element receiving the event
 * @param {Array} nodes - An array of objects containing a name and element
 * @param {string} selectedModifier - The selected modifier value
 * @param {string} filledModifier - The filled modifier value
 * @param {string} invalidModifier - The invalid modifier value
 */
const respond = (type, target, nodes, selectedModifier, filledModifier, invalidModifier) => {
  const { FOCUS, BLUR, INVALID } = EVENTS;

  switch (type) {
    case FOCUS:
      addClassNames(nodes, selectedModifier);
      removeClassNames(nodes, filledModifier, invalidModifier);
      break;
    case BLUR:
      completed(nodes, target, selectedModifier, filledModifier, invalidModifier);
      break;
    case INVALID:
      addClassNames(nodes, invalidModifier);
      break;
    default:
      break;
  }
};

/**
 * Responsible for initializing state modifiers. More specifically, it'll apply
 * any modifiers (i.e. CSS class names in form of BEM) when the page first
 * loads. For example, when a page initially loads and an element is invalid,
 * it'll apply any modifiers. It'll do the same when responding to events based
 * on user interaction.
 * @see DEFAULT_STATE_MODIFIERS
 * @param {Array} nodes - An array of objects containing a name and element
 * @param {Object} options - Options initially passed in
 * @returns {Function} - Used for firing off events
 */
const initialize = (nodes, options = {}) => {
  const {
    filledModifier,
    invalidModifier,
    selectedModifier,
  } = $.extend({}, DEFAULT_STATE_MODIFIERS, options);

  const [{ node: formField }] = nodes.filter(({ type }) => type === TYPES.FIELD);
  completed(nodes, formField, selectedModifier, filledModifier, invalidModifier);

  return (type, { target }) => respond(type, target, nodes,
    selectedModifier, filledModifier, invalidModifier);
};

/**
 * Provides modifiers, i.e. CSS class names in the form of BEM to a block and
 * two child block elements. These elements are coming from the nodes.
 * @see DEFAULT_STATE_MODIFIERS
 * @param options
 */
const stateModifiers = (options = {}) => (...nodes) => initialize(nodes, options);

export default stateModifiers;



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/state-modifiers.js